import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import { cn } from '@/lib/utils'

export type PathOption = {
  id: string
  label: string
  icon: ReactNode
  panel: ReactNode
}

export function PathFinder({ label, options }: { label: string; options: PathOption[] }) {
  const baseId = useId()
  const [selectedId, setSelectedId] = useState(options[0]?.id)
  const tabs = useRef<(HTMLButtonElement | null)[]>([])

  function select(index: number) {
    const option = options[index]
    if (!option) return
    setSelectedId(option.id)
    tabs.current[index]?.focus()
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const last = options.length - 1
    const next: Record<string, number> = {
      ArrowDown: index === last ? 0 : index + 1,
      ArrowRight: index === last ? 0 : index + 1,
      ArrowUp: index === 0 ? last : index - 1,
      ArrowLeft: index === 0 ? last : index - 1,
      Home: 0,
      End: last,
    }
    if (!(event.key in next)) return
    event.preventDefault()
    select(next[event.key])
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)] lg:gap-10">
      <div
        role="tablist"
        aria-label={label}
        aria-orientation="vertical"
        className="flex flex-col gap-2.5"
      >
        {options.map((option, index) => {
          const selected = option.id === selectedId
          return (
            <button
              key={option.id}
              ref={(element) => {
                tabs.current[index] = element
              }}
              type="button"
              role="tab"
              id={`${baseId}-tab-${option.id}`}
              aria-selected={selected}
              aria-controls={`${baseId}-panel-${option.id}`}
              tabIndex={selected ? 0 : -1}
              onClick={() => setSelectedId(option.id)}
              onKeyDown={(event) => onKeyDown(event, index)}
              className={cn(
                'group relative flex min-h-16 items-center gap-4 overflow-hidden rounded-2xl border px-4 py-3 text-left transition-[border-color,background-color,box-shadow] duration-300 sm:px-5',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent-readable)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--surface-bg)]',
                selected
                  ? 'border-[var(--color-brand-accent)]/45 bg-[linear-gradient(100deg,rgba(0,229,255,0.12),rgba(0,229,255,0.02))] shadow-[0_18px_50px_-28px_rgba(0,229,255,0.7)]'
                  : 'border-[var(--surface-border)] bg-[var(--surface-card)]/60 hover:border-[var(--surface-border-strong)] hover:bg-[var(--surface-card)]',
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'flex size-10 shrink-0 items-center justify-center rounded-xl border transition-colors duration-300',
                  selected
                    ? 'border-[var(--color-brand-accent)]/50 bg-[var(--color-brand-accent)]/15 text-[var(--color-brand-accent)]'
                    : 'border-white/10 bg-white/[0.03] text-[var(--surface-muted)] group-hover:text-[var(--text-strong)]',
                )}
              >
                {option.icon}
              </span>
              <span
                className={cn(
                  'text-[15px] font-semibold transition-colors',
                  selected ? 'text-[var(--text-strong)]' : 'text-[var(--surface-fg)]',
                )}
              >
                {option.label}
              </span>
              <span
                aria-hidden="true"
                className={cn(
                  'ml-auto size-2 rounded-full transition-all duration-300',
                  selected
                    ? 'bg-[var(--color-brand-accent)] shadow-[0_0_12px_rgba(0,229,255,0.9)]'
                    : 'bg-white/10',
                )}
              />
            </button>
          )
        })}
      </div>
      {options.map((option) => (
        <div
          key={option.id}
          role="tabpanel"
          id={`${baseId}-panel-${option.id}`}
          aria-labelledby={`${baseId}-tab-${option.id}`}
          hidden={option.id !== selectedId}
          tabIndex={0}
          className="animate-blur-rise rounded-3xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-brand-accent-readable)]"
        >
          {option.panel}
        </div>
      ))}
    </div>
  )
}
